/**
 * Advanced Context Provider Integration
 * 
 * Enhanced context provider system with async support, error handling,
 * caching, conditional execution, and performance optimization.
 */

import { LogNamespace } from './types';
import {
  ContextProvider,
  NamespaceContext,
  NamespaceConfiguration
} from './logger-factory';

/**
 * Enhanced context provider with advanced features
 */
export interface EnhancedContextProvider extends ContextProvider {
  // Execution control
  timeout?: number;
  retries?: number;
  conditions?: ProviderCondition[];
  
  // Caching
  cacheable?: boolean;
  cacheKey?: (context: NamespaceContext) => string;
  cacheTTL?: number;
  
  // Error handling
  errorStrategy?: 'ignore' | 'fallback' | 'throw';
  fallbackValue?: Record<string, any>;
  
  // Dependencies
  dependencies?: string[]; // Names of other providers this depends on
  
  // Performance
  maxExecutionTime?: number;
  
  // Metadata
  description?: string;
  version?: string;
  tags?: string[];
}

/**
 * Provider condition for conditional execution
 */
export interface ProviderCondition {
  type: 'namespace' | 'context' | 'environment' | 'custom';
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'exists' | 'not_exists' | 'custom';
  key?: string;
  value?: any;
  predicate?: (context: NamespaceContext, namespace: LogNamespace) => boolean;
}

/**
 * Provider execution result
 */
export interface ProviderExecutionResult {
  providerId: string;
  success: boolean;
  data?: Record<string, any>;
  error?: Error;
  executionTime: number;
  cached: boolean;
  retryCount: number;
}

/**
 * Context provider cache entry
 */
interface CacheEntry {
  data: Record<string, any>;
  timestamp: number;
  ttl: number;
}

/**
 * Provider execution statistics
 */
export interface ProviderExecutionStats {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  averageExecutionTime: number;
  cacheHitRate: number;
  errorRate: number;
  retryRate: number;
}

/**
 * Advanced context provider manager
 */
export class ContextProviderManager {
  private cache = new Map<string, CacheEntry>();
  private stats = new Map<string, ProviderExecutionStats>();
  private dependencyGraph = new Map<string, string[]>();
  private executionHistory: ProviderExecutionResult[] = [];
  
  constructor(
    private maxCacheSize = 1000,
    private maxHistorySize = 10000,
    private defaultTimeout = 5000,
    private enableMetrics = true
  ) {}

  /**
   * Execute a single context provider with enhanced features
   */
  async executeProvider(
    provider: EnhancedContextProvider,
    context: NamespaceContext,
    namespace: LogNamespace
  ): Promise<ProviderExecutionResult> {
    const startTime = performance.now();
    const result: ProviderExecutionResult = {
      providerId: provider.name,
      success: false,
      executionTime: 0,
      cached: false,
      retryCount: 0
    };

    try {
      // Check conditions
      if (!this.checkConditions(provider, context, namespace)) {
        result.success = true;
        result.data = {};
        result.executionTime = performance.now() - startTime;
        return result;
      }

      // Check cache
      if (provider.cacheable) {
        const cacheKey = this.generateCacheKey(provider, context);
        const cached = this.getFromCache(cacheKey);
        if (cached) {
          result.success = true;
          result.data = cached;
          result.cached = true;
          result.executionTime = performance.now() - startTime;
          return result;
        }
      }

      // Execute with retries
      const maxRetries = provider.retries || 0;
      let lastError: Error | undefined;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const data = await this.executeProviderFunction(provider, context);
          
          // Cache result if cacheable
          if (provider.cacheable && data) {
            const cacheKey = this.generateCacheKey(provider, context);
            this.setCache(cacheKey, data, provider.cacheTTL || 300000); // 5 minutes default
          }

          result.success = true;
          result.data = data;
          result.retryCount = attempt;
          break;
        } catch (error) {
          lastError = error as Error;
          result.retryCount = attempt;
          
          if (attempt === maxRetries) {
            throw error;
          }
          
          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            await this.delay(Math.pow(2, attempt) * 100);
          }
        }
      }
    } catch (error) {
      result.error = error as Error;
      
      // Handle error strategy
      switch (provider.errorStrategy || 'ignore') {
        case 'fallback':
          if (provider.fallbackValue) {
            result.success = true;
            result.data = provider.fallbackValue;
          }
          break;
        case 'throw':
          throw error;
        case 'ignore':
        default:
          result.success = false;
          break;
      }
    } finally {
      result.executionTime = performance.now() - startTime;
      
      // Update statistics
      if (this.enableMetrics) {
        this.updateStats(provider.name, result);
        this.addToHistory(result);
      }
    }

    return result;
  }

  /**
   * Execute multiple providers with dependency resolution
   */
  async executeProviders(
    providers: EnhancedContextProvider[],
    context: NamespaceContext,
    namespace: LogNamespace
  ): Promise<Record<string, any>> {
    // Build dependency graph
    this.buildDependencyGraph(providers);
    
    // Resolve execution order
    const executionOrder = this.resolveDependencies(providers);
    
    const results: Record<string, any> = {};
    const executionResults: ProviderExecutionResult[] = [];

    // Execute providers in dependency order
    for (const provider of executionOrder) {
      try {
        const result = await this.executeProvider(provider, context, namespace);
        executionResults.push(result);
        
        if (result.success && result.data) {
          // Merge provider data into results
          Object.assign(results, result.data);
          
          // Make results available to dependent providers
          context.customMetadata = { ...context.customMetadata, ...results };
        }
      } catch (error) {
        // Provider execution failed, continue with others unless critical
        if (provider.errorStrategy === 'throw') {
          throw error;
        }
      }
    }

    return results;
  }

  /**
   * Check provider execution conditions
   */
  private checkConditions(
    provider: EnhancedContextProvider,
    context: NamespaceContext,
    namespace: LogNamespace
  ): boolean {
    if (!provider.conditions || provider.conditions.length === 0) {
      return true;
    }

    return provider.conditions.every(condition => {
      switch (condition.type) {
        case 'namespace':
          return this.evaluateCondition(namespace, condition.operator, condition.value);
        
        case 'context':
          const contextValue = condition.key ? context.customMetadata[condition.key] : context;
          return this.evaluateCondition(contextValue, condition.operator, condition.value);
        
        case 'environment':
          const envValue = process.env[condition.key || 'NODE_ENV'];
          return this.evaluateCondition(envValue, condition.operator, condition.value);
        
        case 'custom':
          return condition.predicate ? condition.predicate(context, namespace) : true;
        
        default:
          return true;
      }
    });
  }

  /**
   * Evaluate a condition operator
   */
  private evaluateCondition(actual: any, operator: string, expected: any): boolean {
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not_equals':
        return actual !== expected;
      case 'contains':
        return actual && actual.toString().includes(expected);
      case 'not_contains':
        return !actual || !actual.toString().includes(expected);
      case 'exists':
        return actual !== undefined && actual !== null;
      case 'not_exists':
        return actual === undefined || actual === null;
      default:
        return true;
    }
  }

  /**
   * Execute provider function with timeout
   */
  private async executeProviderFunction(
    provider: EnhancedContextProvider,
    context: NamespaceContext
  ): Promise<Record<string, any>> {
    const timeout = provider.timeout || provider.maxExecutionTime || this.defaultTimeout;
    
    return Promise.race([
      Promise.resolve(provider.provide(context)),
      this.createTimeoutPromise(timeout, `Provider '${provider.name}' timed out`)
    ]);
  }

  /**
   * Create a timeout promise
   */
  private createTimeoutPromise<T>(ms: number, message: string): Promise<T> {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    });
  }

  /**
   * Generate cache key for provider
   */
  private generateCacheKey(provider: EnhancedContextProvider, context: NamespaceContext): string {
    if (provider.cacheKey) {
      return provider.cacheKey(context);
    }
    
    // Default cache key generation
    const keyData = {
      providerId: provider.name,
      userId: context.userId,
      sessionId: context.sessionId,
      requestId: context.requestId
    };
    
    return btoa(JSON.stringify(keyData));
  }

  /**
   * Get data from cache
   */
  private getFromCache(key: string): Record<string, any> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache
   */
  private setCache(key: string, data: Record<string, any>, ttl: number): void {
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Build dependency graph for providers
   */
  private buildDependencyGraph(providers: EnhancedContextProvider[]): void {
    this.dependencyGraph.clear();
    
    for (const provider of providers) {
      this.dependencyGraph.set(provider.name, provider.dependencies || []);
    }
  }

  /**
   * Resolve provider dependencies and return execution order
   */
  private resolveDependencies(providers: EnhancedContextProvider[]): EnhancedContextProvider[] {
    const providerMap = new Map(providers.map(p => [p.name, p]));
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const result: EnhancedContextProvider[] = [];

    const visit = (providerName: string): void => {
      if (visited.has(providerName)) return;
      if (visiting.has(providerName)) {
        throw new Error(`Circular dependency detected involving provider: ${providerName}`);
      }

      visiting.add(providerName);
      
      const dependencies = this.dependencyGraph.get(providerName) || [];
      for (const dep of dependencies) {
        if (providerMap.has(dep)) {
          visit(dep);
        }
      }
      
      visiting.delete(providerName);
      visited.add(providerName);
      
      const provider = providerMap.get(providerName);
      if (provider) {
        result.push(provider);
      }
    };

    for (const provider of providers) {
      visit(provider.name);
    }

    return result;
  }

  /**
   * Update provider statistics
   */
  private updateStats(providerId: string, result: ProviderExecutionResult): void {
    let stats = this.stats.get(providerId);
    if (!stats) {
      stats = {
        totalExecutions: 0,
        successfulExecutions: 0,
        failedExecutions: 0,
        averageExecutionTime: 0,
        cacheHitRate: 0,
        errorRate: 0,
        retryRate: 0
      };
      this.stats.set(providerId, stats);
    }

    stats.totalExecutions++;
    
    if (result.success) {
      stats.successfulExecutions++;
    } else {
      stats.failedExecutions++;
    }

    // Update average execution time
    stats.averageExecutionTime = 
      (stats.averageExecutionTime * (stats.totalExecutions - 1) + result.executionTime) / stats.totalExecutions;

    // Update rates
    stats.errorRate = stats.failedExecutions / stats.totalExecutions;
    stats.retryRate = (result.retryCount > 0 ? 1 : 0) / stats.totalExecutions;
    
    // Cache hit rate calculation would need additional tracking
    if (result.cached) {
      stats.cacheHitRate = (stats.cacheHitRate * (stats.totalExecutions - 1) + 1) / stats.totalExecutions;
    } else {
      stats.cacheHitRate = (stats.cacheHitRate * (stats.totalExecutions - 1)) / stats.totalExecutions;
    }
  }

  /**
   * Add execution result to history
   */
  private addToHistory(result: ProviderExecutionResult): void {
    this.executionHistory.push(result);
    
    // Maintain history size limit
    if (this.executionHistory.length > this.maxHistorySize) {
      this.executionHistory.shift();
    }
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get provider statistics
   */
  getProviderStats(providerId?: string): Map<string, ProviderExecutionStats> | ProviderExecutionStats | undefined {
    if (providerId) {
      return this.stats.get(providerId);
    }
    return new Map(this.stats);
  }

  /**
   * Get execution history
   */
  getExecutionHistory(providerId?: string): ProviderExecutionResult[] {
    if (providerId) {
      return this.executionHistory.filter(r => r.providerId === providerId);
    }
    return [...this.executionHistory];
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear statistics
   */
  clearStats(): void {
    this.stats.clear();
    this.executionHistory.length = 0;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number; hitRate: number } {
    const totalHits = Array.from(this.stats.values())
      .reduce((sum, stats) => sum + stats.cacheHitRate * stats.totalExecutions, 0);
    const totalExecutions = Array.from(this.stats.values())
      .reduce((sum, stats) => sum + stats.totalExecutions, 0);
    
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      hitRate: totalExecutions > 0 ? totalHits / totalExecutions : 0
    };
  }
}

/**
 * Helper functions for creating enhanced context providers
 */
export const ContextProviderHelpers = {
  /**
   * Create a conditional provider
   */
  createConditionalProvider(
    baseProvider: ContextProvider,
    conditions: ProviderCondition[]
  ): EnhancedContextProvider {
    return {
      ...baseProvider,
      conditions,
      errorStrategy: 'ignore'
    };
  },

  /**
   * Create a cached provider
   */
  createCachedProvider(
    baseProvider: ContextProvider,
    cacheTTL = 300000, // 5 minutes
    cacheKey?: (context: NamespaceContext) => string
  ): EnhancedContextProvider {
    return {
      ...baseProvider,
      cacheable: true,
      cacheTTL,
      cacheKey,
      errorStrategy: 'fallback',
      fallbackValue: {}
    };
  },

  /**
   * Create a resilient provider with retries
   */
  createResilientProvider(
    baseProvider: ContextProvider,
    retries = 2,
    timeout = 5000
  ): EnhancedContextProvider {
    return {
      ...baseProvider,
      retries,
      timeout,
      errorStrategy: 'fallback',
      fallbackValue: {}
    };
  },

  /**
   * Create a dependent provider
   */
  createDependentProvider(
    baseProvider: ContextProvider,
    dependencies: string[]
  ): EnhancedContextProvider {
    return {
      ...baseProvider,
      dependencies,
      errorStrategy: 'ignore'
    };
  }
};

/**
 * Global context provider manager instance
 */
export const globalContextProviderManager = new ContextProviderManager(); 